// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  buildRangeValue,
  getFirstValueFromCommaSeparated,
  getMultiSelectProps,
  getOperatorOptions,
  getSelectedOperatorOption,
  hasAttributeOptions,
  isListInclusionOperator,
  isMatchingRegexOperator,
} from './helpers';
import { Tooltip } from '@usealloy/component-library';
import { updateThreshold } from 'flow/store/redux/thunks/thresholdUpdates';
import type { DropdownOption } from 'flow/editor/components/NodeEditorDrawer/types';
import { BEST_EFFORT_PARSING_DEFAULT_STRING } from 'flow/service/thresholds';
import { ThresholdContext } from './context';
import { StyledCodeStyleText } from 'flow/editor/styles';
import RuleMultiSelect from '../RuleTemplate/RuleMultiSelect';
import { RuleBuilderContext } from '../../context';
import { StyledEditableControlContainer } from '../styles';
import { selectRuleBuilderActiveSubEditor } from 'flow/store/redux/selectors';
import { getIsPreview } from '../../helpers';
import { UserContext } from '~/dashboard/context';

export interface SelectedOperator {
  value: string;
  meta?: {
    RHS: string | number | null;
  };
}

interface OperatorWrapperProps {
  operatorIsNullOrNotNull: boolean;
}

const OperatorWrapper = ({ operatorIsNullOrNotNull }: OperatorWrapperProps) => {
  const dispatch = useAppDispatch();
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const { featureFlags } = useContext(UserContext);
  const { editMode } = useContext(RuleBuilderContext);
  const { isDynamic, lhsDataType, populatedThreshold } = useContext(ThresholdContext);
  const { left, publicId } = populatedThreshold;

  const selectedOperator = operatorIsNullOrNotNull
    ? { value: populatedThreshold.operator, meta: { RHS: populatedThreshold.right.attributeValue } }
    : { value: populatedThreshold.operator };

  const leftHasAttributeOptions = hasAttributeOptions(left.attribute);

  const operatorOptions = getOperatorOptions({
    actionType: left.action.type,
    isDynamic,
    leftHasAttributeOptions,
    lhsDataType,
    supportsBestEffortParsing: featureFlags.allowBestEffortRuleEvaluations,
  });

  const selectedOperatorOption = getSelectedOperatorOption(
    selectedOperator,
    operatorOptions,
  );

  const updateOperator = (value: string) => {
    dispatch(updateThreshold.operator(publicId, value));
  };

  const updateAttributeValue = (value: string | number | null) => {
    dispatch(updateThreshold.rightValue(publicId, value));
  };

  const handleOperatorChange = (option: DropdownOption) => {
    const { value: newOperator, meta } = option;
    const currentOperator = populatedThreshold.operator;
    const currentRHSValue = populatedThreshold.right.attributeValue;

    updateOperator(newOperator);

    // If switching to null/not null operator with predefined RHS value
    if (meta?.RHS !== undefined) {
      updateAttributeValue(meta.RHS);
      return;
    }

    // Handle switching from null/not-null operators to regular operators
    if (operatorIsNullOrNotNull) {
      const defaultValue = lhsDataType === 'string'
        ? BEST_EFFORT_PARSING_DEFAULT_STRING
        : 0.0;

      updateAttributeValue(defaultValue);
      return;
    }

    if (newOperator === 'range') {
      const rangeValue = buildRangeValue(currentRHSValue, isListInclusionOperator(currentOperator));
      updateAttributeValue(rangeValue);
      return;
    }

    // Handle switching from multi-value operators to single-value operators
    const isFromMultiValueOperator = currentOperator === 'range' || isListInclusionOperator(currentOperator);
    const isToSingleValueOperator = newOperator !== 'range' && !isListInclusionOperator(newOperator);

    if (isFromMultiValueOperator && isToSingleValueOperator) {
      const firstValue = getFirstValueFromCommaSeparated(currentRHSValue);
      updateAttributeValue(firstValue);
      return;
    }

    if (isMatchingRegexOperator({ lhsDataType, operator: newOperator })) {
      updateAttributeValue('');
    }
  };

  const multiSelectProps = getMultiSelectProps({
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  });

  const tooltipContent = useMemo(() => {
    if (operatorIsNullOrNotNull) {
      return (
        <StyledCodeStyleText>
          This refers to empty string, null, or undefined.
        </StyledCodeStyleText>
      );
    }
    return null;
  }, [operatorIsNullOrNotNull]);

  return (
    <Tooltip
      align="start"
      content={tooltipContent}
      side="top"
      variant="secondary"
      hide={!tooltipContent}
    >
      <StyledEditableControlContainer>
        <RuleMultiSelect
          {...multiSelectProps}
          options={operatorOptions}
          onChange={handleOperatorChange}
          selectedOptions={selectedOperatorOption}
          isMulti={false}
          isNaked
          openMenuOnFocus
          placeholder="..."
        />
      </StyledEditableControlContainer>
    </Tooltip>
  );
};

export default OperatorWrapper;

